import {UseQueryOptions} from '@tanstack/react-query';
import {useMemo} from 'react';
import type {
  PreferredCreatorAgeDetail,
  PreferredCreatorCity,
  PreferredCreatorCountry,
  PreferredCreatorGender,
  PreferredCreatorState,
  PreferredCreatorCapability,
  PreferredCreatorSpecialty,
} from '@/types/campaign';
import brandFetchers from '@/api/fetchers/brand';
import useDataQuery from '@/hooks/use-data-query';

/**
 * Valid include options for brand campaigns API.
 * Extend when backend supports more (e.g. segment-related in follow-up PR).
 */
export const BRAND_CAMPAIGNS_INCLUDE_OPTIONS = [
  'preferred_creator_cities',
  'preferred_creator_states',
  'preferred_creator_countries',
  'preferred_creator_age_detail',
  'preferred_creator_genders',
  'preferred_creator_capabilities',
  'preferred_creator_specialties',
  'vertical_tags',
] as const;
export type BrandCampaignsIncludeOption =
  (typeof BRAND_CAMPAIGNS_INCLUDE_OPTIONS)[number];

interface UseBrandCampaignsQueryParams<TData, TError = unknown>
  extends UseQueryOptions<TData, TError> {
  brandId?: string;
  query?: string;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  states?: string[];
  orderByLatest?: boolean;
  include?: readonly BrandCampaignsIncludeOption[];
}

interface SubmittedBy {
  email: string;
  displayName: string | null;
}

interface UnderReviewSubmissionInfo {
  submittedBy: SubmittedBy;
  submittedOn: string | null;
}

export type CampaignListItem = {
  contentAgreementTimePeriodInMonths: number;
  generatedByBriefAi: boolean;
  id: string;
  imageUrl: string;
  name: string;
  network: string;
  state: string;
  underReviewSubmissionInfo?: UnderReviewSubmissionInfo;
  launchedOn: Date;
  preferredCreatorCities?: PreferredCreatorCity[];
  preferredCreatorStates?: PreferredCreatorState[];
  preferredCreatorCountries?: PreferredCreatorCountry[];
  preferredCreatorAgeDetail?: PreferredCreatorAgeDetail;
  preferredCreatorGenders?: PreferredCreatorGender[];
  preferredCreatorCapabilities?: PreferredCreatorCapability[];
  preferredCreatorSpecialties?: PreferredCreatorSpecialty[];
  unlocksAt?: number;
  locksAbove?: number | null;
};

/** @deprecated Import ACTIVE_CAMPAIGN_STATES from '@/types/campaign' instead. */
export {ACTIVE_CAMPAIGN_STATES as ACTIVE_STATES} from '@/types/campaign';

function useBrandCampaignsQuery<TData, TError = unknown>({
  brandId,
  refetchOnWindowFocus = true,
  states,
  orderByLatest = false,
  include,
  ...rest
}: UseBrandCampaignsQueryParams<TData, TError>) {
  const includeKey = useMemo(
    () => (include?.length ? [...include].sort().join(',') : ''),
    [include]
  );
  const response = useDataQuery({
    queryKey: ['brandCampaigns', {brandId, states, include: includeKey}],
    queryFn: brandFetchers.brandCampaigns,
    enabled: !!brandId,
    meta: {
      brandId,
      state: states ? states?.join(',') : undefined,
      include,
    },
    refetchOnWindowFocus,
    ...rest,
  });

  const brandCampaigns = useMemo(() => {
    const campaigns = (response.data || []) as CampaignListItem[];
    if (orderByLatest) {
      campaigns.sort((a, b) => {
        return (
          new Date(b.launchedOn).getTime() - new Date(a.launchedOn).getTime()
        );
      });
    }

    return campaigns;
  }, [response.data, orderByLatest]);

  return {
    ...response,
    brandCampaigns,
  };
}

export default useBrandCampaignsQuery;
